/**
 * Application feature flags.
 *
 * Flags are read from the environment at build time with a hardcoded safe
 * fallback (off) so that a missing or malformed env value never enables a gated
 * capability.
 */

/**
 * Gates the Materials create/edit (CRUD) capability in the Group Dashboard.
 *
 * Stays OFF until backend dependency G6 (Materials create/edit product decision)
 * is confirmed. Enable by setting `REACT_APP_G6_MATERIALS_CRUD=true`.
 */
export const G6_MATERIALS_CRUD: boolean =
  process.env.REACT_APP_G6_MATERIALS_CRUD === 'true';

/**
 * Gates the bulk "remind debtors" action in the Group Dashboard Students tab.
 *
 * Stays OFF until the backend exposes a real reminder/notification mutation.
 * The current per-item action (`checkStudentDebt`) only VALIDATES debt — it
 * does not notify anyone — so claiming «Напоминания отправлены» would be
 * misleading. The button, hook (`useRemindDebtors`), and copy are kept ready
 * behind this flag; enable by setting `REACT_APP_DEBTOR_REMINDERS=true` once
 * the real mutation lands (see docs/groups/backend_tz_group_card_followup.md §B1).
 */
export const DEBTOR_REMINDERS: boolean =
  process.env.REACT_APP_DEBTOR_REMINDERS === 'true';

/**
 * Gates the «Отправить дипломы на почту» action in the Group Dashboard
 * Diplomas tab (spec 0016).
 *
 * Stays OFF until the backend exposes a real diploma-mailing mutation. The
 * button and its copy are built and kept ready behind this flag, but it MUST
 * NOT reach production while there is nothing behind it: the action is
 * side-effecting and irreversible in the operator's mind — someone believing
 * the diplomas were e-mailed when no mail was ever sent is the failure mode
 * this flag exists to prevent. Enable with `REACT_APP_DIPLOMA_SEND_EMAIL=true`
 * once the real mutation lands.
 */
export const DIPLOMA_SEND_EMAIL: boolean =
  process.env.REACT_APP_DIPLOMA_SEND_EMAIL === 'true';

/**
 * Reserved for real server-side diploma generation (spec 0016).
 *
 * Stays OFF until generation actually exists. Until then the Diplomas tab
 * renders «Сгенерировать дипломы» unconditionally, but it only opens a modal
 * that states plainly that generation is not implemented yet and that the
 * linked document is a sample — this flag is what will later switch that modal
 * over to the real thing. Enable with `REACT_APP_DIPLOMA_GENERATION=true`.
 */
export const DIPLOMA_GENERATION: boolean =
  process.env.REACT_APP_DIPLOMA_GENERATION === 'true';

/**
 * Gates «Выставить фактуру» — issuing a REAL Fakturownia VAT document from
 * the student card.
 *
 * OFF by default and deliberately so: every other money action in this app is
 * reversible from the ERP, this one is not. The created document receives a
 * fiscal number, is visible to the client, and nothing in this system can
 * delete it. Keeping the flag off until an operator has been briefed (and the
 * backend's `FAKTUROWNIA_ISSUE_ENABLED` + exempt-reason settings are
 * configured for the environment) means a stray click cannot mint a document
 * on a half-configured install.
 *
 * Enable by setting `REACT_APP_INVOICE_ISSUE=true`. Note the backend gates
 * independently — with the server flag off the preflight answers
 * `INTEGRATION_DISABLED` and the button explains itself.
 */
export const INVOICE_ISSUE: boolean =
  process.env.REACT_APP_INVOICE_ISSUE === 'true';
